/**
 * The tree, on the left of the screen: find a node, open it, add beneath it.
 *
 * A real `<ul>` per level, so a screen-reader user hears the shape, and each
 * row is a button that selects the node — the panel on the right shows it.
 * Folding is the browser's job only in the sense that the toggles carry
 * `aria-expanded`; which nodes are open is state here, so a search can open
 * every branch that leads to a match and closing the search gives the reader
 * back the tree as they left it.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Input } from "@hardauto/core-ui";

import type { CategoryNode, FamilyMapping } from "../../api";
import { NAMESPACE } from "../../i18n";
import { MAX_DEPTH, filterTree } from "./tree";

export interface CategoryTreeProps {
  roots: CategoryNode[];
  locale: string;
  selectedId: string | null;
  families: Map<string, FamilyMapping[]>;
  unmapped: Set<string>;
  levelLabel: (depth: number) => string;
  mayWrite: boolean;
  onSelect: (node: CategoryNode) => void;
  onAddChild: (parent: CategoryNode) => void;
}

export function CategoryTree(props: CategoryTreeProps) {
  const { t } = useTranslation(NAMESPACE);
  const [query, setQuery] = useState("");
  // Roots open by default: the first screen shows activities and their
  // families, which is how people name a place in the catalogue.
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(props.roots.map((node) => node.category.id)),
  );

  const searching = query.trim().length > 0;
  const shown = filterTree(props.roots, query);

  function toggle(id: string) {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="hardauto-cattree">
      <div className="hardauto-cattree__search">
        <Input
          type="search"
          aria-label={t("tree.searchLabel")}
          placeholder={t("tree.searchPlaceholder")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {shown.length === 0 ? (
        <p className="hardauto-cell__muted hardauto-cattree__none">
          {t("tree.noMatch", { query: query.trim() })}
        </p>
      ) : (
        <Level
          {...props}
          nodes={shown}
          isOpen={(id) => searching || open.has(id)}
          onToggle={toggle}
        />
      )}
    </div>
  );
}

function Level(
  props: CategoryTreeProps & {
    nodes: CategoryNode[];
    isOpen: (id: string) => boolean;
    onToggle: (id: string) => void;
  },
) {
  const { t } = useTranslation(NAMESPACE);
  const { nodes, locale, selectedId, families, unmapped, levelLabel, mayWrite } = props;
  return (
    <ul className="hardauto-cattree__list">
      {nodes.map((node) => {
        const { category } = node;
        const hasChildren = node.children.length > 0;
        const expanded = hasChildren && props.isOpen(category.id);
        const familyCount = families.get(category.id)?.length ?? 0;
        const selected = category.id === selectedId;
        const level = levelLabel(category.depth);
        return (
          <li key={category.id}>
            <div
              className="hardauto-cattree__row"
              data-selected={selected || undefined}
              data-inactive={category.is_active ? undefined : true}
            >
              {hasChildren ? (
                <button
                  type="button"
                  className="hardauto-cattree__toggle"
                  aria-expanded={expanded}
                  aria-label={t(expanded ? "tree.collapse" : "tree.expand", {
                    label: category.label,
                  })}
                  onClick={() => props.onToggle(category.id)}
                >
                  <span className="hardauto-cattree__chevron" aria-hidden="true" />
                </button>
              ) : (
                <span className="hardauto-cattree__toggle" aria-hidden="true" />
              )}
              <button
                type="button"
                className="hardauto-cattree__select"
                aria-current={selected ? "true" : undefined}
                onClick={() => props.onSelect(node)}
              >
                {level ? <span className="hardauto-cattree__level">{level}</span> : null}
                <span className="hardauto-cattree__label">{category.label}</span>
              </button>
              <span className="hardauto-cattree__meta">
                {category.is_active ? null : <Badge tone="neutral">{t("tree.hidden")}</Badge>}
                {unmapped.has(category.id) ? (
                  <Badge tone="warning">{t("tree.unmapped")}</Badge>
                ) : null}
                {familyCount > 0 ? (
                  <span className="hardauto-cattree__count" title={t("tree.familiesTitle")}>
                    {t("tree.families", { count: familyCount })}
                  </span>
                ) : null}
                <span
                  className="hardauto-num hardauto-cattree__count"
                  title={t("tree.articlesTitle")}
                >
                  {node.article_count.toLocaleString(locale)}
                </span>
              </span>
              {mayWrite && category.depth < MAX_DEPTH ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="hardauto-cattree__add"
                  aria-label={t("tree.addChild", { label: category.label })}
                  onClick={() => props.onAddChild(node)}
                >
                  +
                </Button>
              ) : (
                <span className="hardauto-cattree__add" aria-hidden="true" />
              )}
            </div>
            {expanded ? <Level {...props} nodes={node.children} /> : null}
          </li>
        );
      })}
    </ul>
  );
}
